import React from "react";
import { useLanguage } from "@/context/LanguageContext";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";

const COPY = {
  pt: {
    overline: "Aulas de francês para todos os níveis",
    seoH2: "Planos de curso · A1 a C2",
    title: "Do A1 ao C2 — em qual nível você está?",
    body: "O Quadro Europeu Comum de Referência (CEFR) divide o francês em 6 níveis. Identifique o seu e veja como o Prof Lionel pode te levar ao próximo.",
    levels: [
      { id: "a1", code: "A1", name: "Iniciante", desc: "Você reconhece frases simples e do dia a dia." },
      { id: "a2", code: "A2", name: "Básico", desc: "Você se vira em situações cotidianas." },
      { id: "b1", code: "B1", name: "Intermediário", desc: "Você fala em viagens e sobre temas conhecidos." },
      { id: "b2", code: "B2", name: "Avançado", desc: "Você argumenta com fluência. DELF B2 ao alcance." },
      { id: "c1", code: "C1", name: "Autônomo", desc: "Você domina o francês com sutileza. Foco em DALF C1." },
      { id: "c2", code: "C2", name: "Maestria", desc: "Nível quase nativo. Refinamento estilístico e literário." },
    ],
    cta: "Fazer o teste de nível grátis",
  },
  en: {
    overline: "French lessons for every level",
    seoH2: "Course plans · A1 to C2",
    title: "From A1 to C2 — what level are you?",
    body: "The CEFR splits French into 6 levels. Find yours and see how Prof Lionel can take you to the next.",
    levels: [
      { id: "a1", code: "A1", name: "Beginner", desc: "You recognise everyday simple sentences." },
      { id: "a2", code: "A2", name: "Elementary", desc: "You cope with everyday situations." },
      { id: "b1", code: "B1", name: "Intermediate", desc: "You handle travel and familiar topics." },
      { id: "b2", code: "B2", name: "Upper-intermediate", desc: "You argue fluently. DELF B2 within reach." },
      { id: "c1", code: "C1", name: "Proficient", desc: "You master French with subtlety. Focus on DALF C1." },
      { id: "c2", code: "C2", name: "Mastery", desc: "Near-native. Stylistic and literary refinement." },
    ],
    cta: "Take the free level test",
  },
  fr: {
    overline: "Cours de français pour tous les niveaux",
    seoH2: "Plans de cours · A1 à C2",
    title: "Du A1 au C2 — quel est votre niveau ?",
    body: "Le CECRL divise le français en 6 niveaux. Identifiez le vôtre et voyez comment le Prof Lionel peut vous mener au suivant.",
    levels: [
      { id: "a1", code: "A1", name: "Débutant", desc: "Vous reconnaissez des phrases simples du quotidien." },
      { id: "a2", code: "A2", name: "Élémentaire", desc: "Vous gérez le quotidien." },
      { id: "b1", code: "B1", name: "Intermédiaire", desc: "Vous discutez en voyage et de sujets familiers." },
      { id: "b2", code: "B2", name: "Avancé", desc: "Vous argumentez avec aisance. DELF B2 à portée." },
      { id: "c1", code: "C1", name: "Autonome", desc: "Vous maîtrisez avec nuance. Cap sur le DALF C1." },
      { id: "c2", code: "C2", name: "Maîtrise", desc: "Quasi-natif. Raffinement stylistique et littéraire." },
    ],
    cta: "Faire le test de niveau gratuit",
  },
};

const LEVEL_COLORS = {
  a1: "#9c9c9c",
  a2: "#5c8aa6",
  b1: "#1e2a5a",
  b2: "#7a4a8a",
  c1: "#d9381e",
  c2: "#3a3a3a",
};

// Paris-themed cover imagery per level — verified working URLs (used elsewhere in the app)
const LEVEL_IMAGES = {
  a1: "https://images.unsplash.com/photo-1473951574080-01fe45ec8643?auto=format&fit=crop&w=800&q=75", // Boulangerie / bakery
  a2: "https://images.unsplash.com/photo-1522093007474-d86e9bf7ba6f?auto=format&fit=crop&w=800&q=75", // Paris café
  b1: "https://images.unsplash.com/photo-1520939817895-060bdaf4fe1b?auto=format&fit=crop&w=800&q=75", // Rue de Paris (Montmartre)
  b2: "https://images.unsplash.com/photo-1502602898657-3e91760cbb34?auto=format&fit=crop&w=800&q=75", // Tour Eiffel
  c1: "https://images.unsplash.com/photo-1499856871958-5b9627545d1a?auto=format&fit=crop&w=800&q=75", // Pont Alexandre III · Paris by night
  c2: "https://images.unsplash.com/photo-1549144511-f099e773c147?auto=format&fit=crop&w=800&q=75", // Study / Sorbonne feel
};

const Niveis = () => {
  const { lang } = useLanguage();
  const t = COPY[lang] || COPY.pt;
  return (
    <section
      id="niveis"
      data-testid="niveis-section"
      className="bg-[#f4f1ea] py-24 sm:py-32 border-t border-[#e5e3db]"
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-12">
        <div className="max-w-2xl">
          <h2 className="overline">{t.seoH2}</h2>
          <p className="font-display mt-4 text-4xl sm:text-5xl tracking-tight text-[#1e2a5a]">
            {t.title}
          </p>
          <p className="mt-6 text-base sm:text-lg text-[#5c5c5c] leading-relaxed max-w-xl">
            {t.body}
          </p>
        </div>

        <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {t.levels.map((lv) => (
            <article
              key={lv.id}
              id={lv.id}
              data-testid={`level-card-${lv.id}`}
              className="group scroll-mt-24 overflow-hidden bg-white border border-[#e5e3db] rounded-xl transition-all hover:border-[#1e2a5a]/40 hover:shadow-lg hover:-translate-y-0.5"
            >
              {/* Uppercase anchor alias (e.g. /#A1) for SEO + user-friendly URLs */}
              <span
                id={lv.code}
                aria-hidden="true"
                className="block -mt-24 pt-24 pointer-events-none"
              />
              {/* Paris-themed thumbnail */}
              <div className="relative h-40 overflow-hidden bg-[#f0efea]">
                <img
                  src={LEVEL_IMAGES[lv.id]}
                  alt={`${lv.code} · ${lv.name}`}
                  loading="lazy"
                  decoding="async"
                  className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent" />
                <span
                  className="absolute top-3 left-3 inline-flex items-center justify-center min-w-[44px] px-3 py-1 rounded-full text-sm font-semibold text-white shadow-md"
                  style={{ backgroundColor: LEVEL_COLORS[lv.id] }}
                >
                  {lv.code}
                </span>
                <span className="absolute bottom-3 left-3 text-xs uppercase tracking-[0.18em] text-white/95 font-medium drop-shadow">
                  {lv.name}
                </span>
              </div>
              <div className="p-6">
                <p className="text-sm text-[#3a3a3a] leading-relaxed">{lv.desc}</p>
              </div>
            </article>
          ))}
        </div>

        <Link
          to="/teste-de-nivel"
          data-testid="niveis-test-cta"
          className="group mt-12 inline-flex items-center gap-2 rounded-full border border-[#1e2a5a] px-5 py-2.5 text-sm font-medium text-[#1e2a5a] transition-colors hover:bg-[#1e2a5a] hover:text-[#f4f1ea]"
        >
          {t.cta}
          <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
        </Link>
      </div>
    </section>
  );
};

export default Niveis;
